import {
  getBucketSizeMs,
  truncateToPeriod,
} from "@/App/Equities/shared/services/marketData/barBucketing"
import { TimeUnit } from "@/App/Equities/shared/utils"

import {
  type BarResponse,
  type EquityQuoteRequest,
  type EquityQuoteResponse,
  type HistoricalBarRequest,
  type HistoricalBarResult,
  type LatestBarRequest,
  type LatestBarResult,
  type QuoteResult,
  SUCCESS_HISTORICAL_BAR_RESULT,
  SUCCESS_LATEST_BAR_RESULT,
  SUCCESS_QUOTE_RESULT,
  type SuccessHistoricalBarResult,
  type SuccessLatestBarResult,
  type SuccessQuoteResult,
} from "../MarketDataService"

const SYMBOL = "META"
const BASE_PRICE = 100
// Per-tick move. getLatestBars polls at 1Hz, so this compounds ~600 times
// across a 10m bucket — sizing it per bucket makes the candle explode.
const TICK_VOLATILITY = 0.1

// Centered on zero, so the walk drifts by at most +/- TICK_VOLATILITY / 2 per poll.
const getNextPrice = (previousPrice: number) =>
  previousPrice + Math.random() * TICK_VOLATILITY - TICK_VOLATILITY / 2

type Ohlc = { o: number; h: number; l: number; c: number }
const createOhlc = (base: number = BASE_PRICE): Ohlc => {
  const volatility = 5
  const o = base + Math.random() * volatility - volatility / 2
  const c = base + Math.random() * volatility - volatility / 2
  const h = Math.max(o, c) + Math.random()
  const l = Math.min(o, c) - Math.random()

  return { o, h, l, c }
}

const createMockBarData = (
  timestamp: Date,
  previousClose: number,
  symbol: string,
): BarResponse => {
  const { o, h, l, c } = createOhlc(previousClose)
  const v = Math.floor(Math.random() * 2_000_000)
  const n = Math.floor(Math.random() * 20_000)
  const vw = l + (h - l) * Math.random()
  return {
    o: o.toString(),
    h: h.toString(),
    l: l.toString(),
    c: c.toString(),
    v,
    n,
    vw: vw.toString(),
    symbol: symbol || SYMBOL,
    t: timestamp.toISOString(),
  }
}

const parseTimeframe = (
  timeframe: string,
): { timeUnit: TimeUnit; interval: number } => {
  const match = /^(\d+)(Min|Hour|Day)$/.exec(timeframe)
  if (!match) return { timeUnit: TimeUnit.Day, interval: 1 }
  return { timeUnit: match[2] as TimeUnit, interval: Number(match[1]) }
}

// The in-progress minute bar per symbol, mirroring what Alpaca's latest-bar
// endpoint returns: one bar per minute that keeps accumulating until the
// minute rolls over.
type LiveBar = {
  minute: number
  o: number
  h: number
  l: number
  c: number
  v: number
  n: number
  notional: number
}

const liveBars = new Map<string, LiveBar>()

const getLivePrice = (symbol: string) => liveBars.get(symbol)?.c ?? BASE_PRICE

const DEFAULT_BAR_COUNT = 300

type CachedSeries = { bucket: number; bars: BarResponse[] }

// The real gateway serves every caller the same Alpaca data, so two callers
// asking for the same symbol and timeframe must not see different randomness.
// Generated once per symbol+timeframe and regenerated when the bucket rolls.
const seriesCache = new Map<string, CachedSeries>()

const generateSeries = (
  symbol: string,
  currentBucket: number,
  bucketMs: number,
): BarResponse[] => {
  // Walk backwards from the live price so the newest candle lines up with the
  // live bar instead of being an unrelated random series.
  let previousClose = getLivePrice(symbol)

  return Array.from({ length: DEFAULT_BAR_COUNT }, (_, index) => {
    const mockHistoricalBar = createMockBarData(
      new Date(currentBucket - index * bucketMs),
      previousClose,
      symbol,
    )
    previousClose = Number.parseFloat(mockHistoricalBar.c)

    return mockHistoricalBar
  })
}

const createMockHistoricalBars = (
  symbol: string,
  timeframe: string,
  barCount: number,
): BarResponse[] => {
  const { timeUnit, interval } = parseTimeframe(timeframe)
  const bucketMs = getBucketSizeMs(timeUnit, interval)
  // Newest bar is the in-progress bucket, so the first live tick merges into it
  // instead of leaving a gap between the last historical and the live candle.
  const currentBucket = truncateToPeriod(
    new Date().toISOString(),
    timeUnit,
    interval,
  )

  const key = `${symbol}|${timeframe}`
  const cached = seriesCache.get(key)

  if (cached?.bucket === currentBucket) {
    return cached.bars.slice(0, barCount)
  }

  const bars = generateSeries(symbol, currentBucket, bucketMs)
  seriesCache.set(key, { bucket: currentBucket, bars })

  return bars.slice(0, barCount)
}

export function createMockHistoricalBarData(
  input: HistoricalBarRequest,
): HistoricalBarResult[] {
  const symbols = input.symbols
    .split(",")
    .map((symbol) => symbol.trim())
    .filter(Boolean)

  // Alpaca applies `limit` across the whole request rather than per symbol;
  // splitting it evenly keeps every requested symbol represented.
  const barCount = Math.min(
    DEFAULT_BAR_COUNT,
    input.limit > 0
      ? Math.max(2, Math.floor(input.limit / symbols.length))
      : DEFAULT_BAR_COUNT,
  )

  return symbols.flatMap((symbol) =>
    createMockHistoricalBars(symbol, input.timeframe, barCount).map(
      (bar) =>
        ({
          type: SUCCESS_HISTORICAL_BAR_RESULT,
          payload: bar,
        }) satisfies SuccessHistoricalBarResult,
    ),
  )
}

export function createMockLatestBarData(
  input: LatestBarRequest,
): LatestBarResult {
  const symbol = input.symbols
  const minute = truncateToPeriod(new Date().toISOString(), TimeUnit.Min, 1)
  const previous = liveBars.get(symbol)

  const price = getNextPrice(previous?.c ?? BASE_PRICE)
  const volume = Math.floor(Math.random() * 5_000)
  const trades = Math.floor(Math.random() * 50)

  const open = previous?.minute === minute ? previous : undefined
  const bar: LiveBar = {
    minute,
    o: open?.o ?? price,
    h: Math.max(open?.h ?? price, price),
    l: Math.min(open?.l ?? price, price),
    c: price,
    v: (open?.v ?? 0) + volume,
    n: (open?.n ?? 0) + trades,
    notional: (open?.notional ?? 0) + price * volume,
  }
  liveBars.set(symbol, bar)

  return {
    type: SUCCESS_LATEST_BAR_RESULT,
    payload: {
      o: bar.o.toString(),
      h: bar.h.toString(),
      l: bar.l.toString(),
      c: bar.c.toString(),
      v: bar.v,
      n: bar.n,
      vw: (bar.v > 0 ? bar.notional / bar.v : price).toString(),
      symbol: symbol || SYMBOL,
      t: new Date(minute).toISOString(),
    } satisfies BarResponse,
  } satisfies SuccessLatestBarResult
}

const SPREAD_BPS = 2
const createMockQuoteData = (
  input: EquityQuoteRequest,
): EquityQuoteResponse => {
  const symbol = input.symbols
  const mid = getLivePrice(symbol)
  const halfSpread = mid * (SPREAD_BPS / 10_000) * 0.5

  return {
    ap: (mid + halfSpread).toString(),
    bp: (mid - halfSpread).toString(),
    t: new Date().toISOString(),
    symbol,
  }
}

export function createMockQuote(input: EquityQuoteRequest): QuoteResult {
  const successResponse: EquityQuoteResponse = {
    ...createMockQuoteData(input),
    symbol: input.symbols,
  }

  return {
    type: SUCCESS_QUOTE_RESULT,
    payload: successResponse,
  } satisfies SuccessQuoteResult
}
